import { Constants } from '../constants';
import { AppTabs } from '../constants/constants';
import { BattlePressureLevel, KratosTokenKey, KratosTokens, LoadoutStats, PrimordialStats, SimulationConfig, SimulationMode } from '../types';

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export interface AppUiState {
    activeTab?: AppTabs | null;
    simulatorInputTab?: AppTabs | null;
    simulationMode?: SimulationMode;
    simulatorGuideExpanded?: boolean;
}

export interface SimulatorState {
    primordialStats?: PrimordialStats;
    kratosPool?: KratosTokens;
    simulationConfig?: SimulationConfig;
    loadoutStats?: LoadoutStats[];
    visibleTags?: string[];
    advancedMode?: boolean;
}

export interface BattlefieldPreferences {
    primordialUseCalculator?: boolean;
    selectedPrimordialName?: string;
    selectedPrimordialLevel?: number;
    selectedBPLevel?: BattlePressureLevel;
    compareSelectedTokens?: KratosTokenKey[];
}

const PERSISTED_PANEL_PREFERENCE_KEYS = [
    Constants.LocalStorageKeys.SIMULATOR_SETTINGS_EXPANDED,
    Constants.LocalStorageKeys.SPOILER_SETTINGS_EXPANDED,
    Constants.LocalStorageKeys.FAVORITE_LOADOUTS_EXPANDED,
];

export function loadAppUiState(storage: StorageLike = localStorage): AppUiState {
    const grouped = parseStoredJson<AppUiState>(storage.getItem(Constants.LocalStorageKeys.APP_UI_STATE));
    return {
        activeTab: grouped?.activeTab,
        simulatorInputTab: grouped?.simulatorInputTab,
        simulationMode: grouped?.simulationMode,
        simulatorGuideExpanded: grouped?.simulatorGuideExpanded,
    };
}

export function saveAppUiState(state: AppUiState, storage: StorageLike = localStorage) {
    storage.setItem(Constants.LocalStorageKeys.APP_UI_STATE, JSON.stringify(state));
}

export function loadSimulatorState(storage: StorageLike = localStorage): SimulatorState {
    const grouped = parseStoredJson<SimulatorState>(storage.getItem(Constants.LocalStorageKeys.SIMULATOR_STATE));
    return {
        primordialStats: grouped?.primordialStats,
        kratosPool: grouped?.kratosPool,
        simulationConfig: grouped?.simulationConfig,
        loadoutStats: grouped?.loadoutStats,
        visibleTags: grouped?.visibleTags,
        advancedMode: grouped?.advancedMode,
    };
}

export function saveSimulatorState(state: SimulatorState, storage: StorageLike = localStorage) {
    storage.setItem(Constants.LocalStorageKeys.SIMULATOR_STATE, JSON.stringify(state));
}

export function loadBattlefieldPreferences(storage: StorageLike = localStorage): BattlefieldPreferences {
    const grouped = parseStoredJson<BattlefieldPreferences>(storage.getItem(Constants.LocalStorageKeys.BATTLEFIELD_PREFERENCES));
    return {
        primordialUseCalculator: grouped?.primordialUseCalculator,
        selectedPrimordialName: grouped?.selectedPrimordialName,
        selectedPrimordialLevel: grouped?.selectedPrimordialLevel,
        selectedBPLevel: grouped?.selectedBPLevel,
        compareSelectedTokens: grouped?.compareSelectedTokens,
    };
}

export function saveBattlefieldPreferences(preferences: BattlefieldPreferences, storage: StorageLike = localStorage) {
    storage.setItem(Constants.LocalStorageKeys.BATTLEFIELD_PREFERENCES, JSON.stringify(preferences));
}

export function clearStoredUiState(storage: StorageLike = localStorage) {
    [
        Constants.LocalStorageKeys.APP_UI_STATE,
        Constants.LocalStorageKeys.SIMULATOR_STATE,
        Constants.LocalStorageKeys.BATTLEFIELD_PREFERENCES,
        Constants.LocalStorageKeys.ACTIVE_TAB,
        Constants.LocalStorageKeys.SIMULATOR_INPUT_TAB,
        Constants.LocalStorageKeys.SIMULATION_MODE,
        Constants.LocalStorageKeys.SIMULATOR_GUIDE_EXPANDED,
        Constants.LocalStorageKeys.PRIMORDIAL_STATS,
        Constants.LocalStorageKeys.KRATOS_POOL,
        Constants.LocalStorageKeys.SIMULATION_CONFIG,
        Constants.LocalStorageKeys.LOADOUT_STATS,
        Constants.LocalStorageKeys.VISIBLE_TAGS,
        Constants.LocalStorageKeys.ADVANCED_MODE,
        Constants.LocalStorageKeys.PRIMORDIAL_USE_CALCULATOR,
        Constants.LocalStorageKeys.SELECTED_PRIMORDIAL_NAME,
        Constants.LocalStorageKeys.SELECTED_PRIMORDIAL_LEVEL,
        Constants.LocalStorageKeys.SELECTED_BP_LEVEL,
        Constants.LocalStorageKeys.COMPARE_SELECTED_TOKENS,
        ...PERSISTED_PANEL_PREFERENCE_KEYS,
    ].forEach((key) => storage.removeItem(key));
}

function parseStoredJson<T>(value: string | null): T | null {
    if (!value) {
        return null;
    }

    try {
        return JSON.parse(value) as T;
    } catch {
        return null;
    }
}
